import type { BiggestGames, BiggestGameBase, BiggestGameWithCheckmate, BiggestGameWithTimeSpent, BiggestGameWithMoves, BiggestGameWithMyTimeLeft, BiggestGameWithOppTimeLeft } from '@/types/stats';
import { isBiggestGameError } from '@/types/stats';

interface Props {
  games: BiggestGames;
}

interface CardProps {
  emoji: string;
  title: string;
  username: string;
  rating: number;
  timeClass: string;
  date: string;
  link: string;
  ctaEmoji: string;
  /** Optional extra detail line (e.g. "12s | 8 moves"). */
  extra?: string;
  bgOpacity: number;
}

const SECTION_HEADER_STYLE: React.CSSProperties = {
  background: 'linear-gradient(135deg, #2D6056 0%, #4A7C59 100%)',
  padding: 15,
  borderRadius: 10,
  margin: '30px 0 20px 0',
  textAlign: 'center',
  fontSize: 24,
  color: '#EEEED5',
  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
};

const LINK_CLASS = 'highlight-game-link';

function HighlightCard({
  emoji,
  title,
  username,
  rating,
  timeClass,
  date,
  link,
  ctaEmoji,
  extra,
  bgOpacity,
}: CardProps) {
  return (
    <div
      className="stat-box2"
      style={{
        background: `linear-gradient(135deg, rgba(45, 96, 86, ${bgOpacity}) 0%, rgba(74, 124, 89, ${bgOpacity}) 100%)`,
        border: '2px solid #EEEED5',
        boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div style={{ position: 'absolute', top: 10, right: 10, fontSize: 24 }}>{emoji}</div>
      <div
        className="stat-title"
        style={{ color: '#EEEED5', fontSize: 18, fontWeight: 700, marginBottom: 10 }}
      >
        {title}
      </div>
      <div
        className="stat-opponent"
        style={{ fontSize: 16, fontWeight: 600, color: '#EEEED5', marginBottom: 8 }}
      >
        @{username}
      </div>
      <div
        className="stat-details"
        style={{ fontSize: 14, color: '#EEEED5', marginBottom: 6 }}
      >
        Rating: {rating} | {timeClass}
      </div>
      {extra && (
        <div
          className="stat-details"
          style={{ fontSize: 14, color: '#EEEED5', marginBottom: 6 }}
        >
          {extra}
        </div>
      )}
      <div
        className="stat-date"
        style={{ fontSize: 12, color: '#EEEED5', marginBottom: 10 }}
      >
        {date}
      </div>
      <a href={link} target="_blank" rel="noopener noreferrer" className={`stat-link ${LINK_CLASS}`}>
        {ctaEmoji} View Game
      </a>
    </div>
  );
}

export default function BiggestGamesSection({ games }: Props) {
  return (
    <>
      <div className="stat-title stat-section-header" style={SECTION_HEADER_STYLE}>
        🏆 SPECIAL GAME HIGHLIGHTS 🏆
      </div>

      <div className="row row-2" style={{ marginBottom: 20 }}>
        {!isBiggestGameError(games.victory) && (
          <HighlightCard
            emoji="🥇"
            title="BIGGEST VICTORY"
            username={(games.victory as BiggestGameBase).opp_username}
            rating={(games.victory as BiggestGameBase).opp_rating}
            timeClass={(games.victory as BiggestGameBase).time_class}
            date={(games.victory as BiggestGameBase).date}
            link={(games.victory as BiggestGameBase).link}
            ctaEmoji="🎯"
            bgOpacity={0.9}
          />
        )}
        {!isBiggestGameError(games.upset) && (
          <HighlightCard
            emoji="😱"
            title="BIGGEST UPSET"
            username={(games.upset as BiggestGameBase).opp_username}
            rating={(games.upset as BiggestGameBase).opp_rating}
            timeClass={(games.upset as BiggestGameBase).time_class}
            date={(games.upset as BiggestGameBase).date}
            link={(games.upset as BiggestGameBase).link}
            ctaEmoji="⚡"
            bgOpacity={0.8}
          />
        )}
      </div>

      <div className="row row-2" style={{ marginBottom: 20 }}>
        {!isBiggestGameError(games.checkmate) && (
          <HighlightCard
            emoji="⚔️"
            title="QUICKEST CHECKMATE"
            username={(games.checkmate as BiggestGameWithCheckmate).opp_username}
            rating={(games.checkmate as BiggestGameWithCheckmate).opp_rating}
            timeClass={(games.checkmate as BiggestGameWithCheckmate).time_class}
            date={(games.checkmate as BiggestGameWithCheckmate).date}
            link={(games.checkmate as BiggestGameWithCheckmate).link}
            ctaEmoji="🗡️"
            extra={`⏱️ ${(games.checkmate as BiggestGameWithCheckmate).time_spent}s | 🎯 ${(games.checkmate as BiggestGameWithCheckmate).my_num_moves} moves`}
            bgOpacity={0.7}
          />
        )}
        {!isBiggestGameError(games.opponent) && (
          <HighlightCard
            emoji="👑"
            title="HIGHEST RATED OPPONENT"
            username={(games.opponent as BiggestGameBase).opp_username}
            rating={(games.opponent as BiggestGameBase).opp_rating}
            timeClass={(games.opponent as BiggestGameBase).time_class}
            date={(games.opponent as BiggestGameBase).date}
            link={(games.opponent as BiggestGameBase).link}
            ctaEmoji="👑"
            bgOpacity={0.6}
          />
        )}
      </div>

      <div className="row row-2" style={{ marginBottom: 20 }}>
        {!isBiggestGameError(games.longest) && (
          <HighlightCard
            emoji="⏰"
            title="LONGEST GAME"
            username={(games.longest as BiggestGameWithTimeSpent).opp_username}
            rating={(games.longest as BiggestGameWithTimeSpent).opp_rating}
            timeClass={(games.longest as BiggestGameWithTimeSpent).time_class}
            date={(games.longest as BiggestGameWithTimeSpent).date}
            link={(games.longest as BiggestGameWithTimeSpent).link}
            ctaEmoji="⏰"
            extra={`⏱️ ${(games.longest as BiggestGameWithTimeSpent).time_spent} seconds`}
            bgOpacity={0.5}
          />
        )}
        {!isBiggestGameError(games.moves) && (
          <HighlightCard
            emoji="🎲"
            title="MOST MOVES GAME"
            username={(games.moves as BiggestGameWithMoves).opp_username}
            rating={(games.moves as BiggestGameWithMoves).opp_rating}
            timeClass={(games.moves as BiggestGameWithMoves).time_class}
            date={(games.moves as BiggestGameWithMoves).date}
            link={(games.moves as BiggestGameWithMoves).link}
            ctaEmoji="🎲"
            extra={`🎯 ${(games.moves as BiggestGameWithMoves).my_num_moves} moves`}
            bgOpacity={0.4}
          />
        )}
      </div>

      <div className="row row-2" style={{ marginBottom: 20 }}>
        {!isBiggestGameError(games.least_time_won) && (
          <HighlightCard
            emoji="🔥"
            title="MOST INTENSE GAME"
            username={(games.least_time_won as BiggestGameWithMyTimeLeft).opp_username}
            rating={(games.least_time_won as BiggestGameWithMyTimeLeft).opp_rating}
            timeClass={(games.least_time_won as BiggestGameWithMyTimeLeft).time_class}
            date={(games.least_time_won as BiggestGameWithMyTimeLeft).date}
            link={(games.least_time_won as BiggestGameWithMyTimeLeft).link}
            ctaEmoji="🔥"
            extra={`⏱️ ${(games.least_time_won as BiggestGameWithMyTimeLeft).my_time_left}s left`}
            bgOpacity={0.3}
          />
        )}
        {!isBiggestGameError(games.least_time_lost) && (
          <HighlightCard
            emoji="😔"
            title="MOST DISAPPOINTING GAME"
            username={(games.least_time_lost as BiggestGameWithOppTimeLeft).opp_username}
            rating={(games.least_time_lost as BiggestGameWithOppTimeLeft).opp_rating}
            timeClass={(games.least_time_lost as BiggestGameWithOppTimeLeft).time_class}
            date={(games.least_time_lost as BiggestGameWithOppTimeLeft).date}
            link={(games.least_time_lost as BiggestGameWithOppTimeLeft).link}
            ctaEmoji="😔"
            extra={`⏱️ ${(games.least_time_lost as BiggestGameWithOppTimeLeft).opp_time_left}s left`}
            bgOpacity={0.2}
          />
        )}
      </div>
    </>
  );
}
